import { useState } from "react";
import "rsuite/dist/rsuite.css";
import DateRangePicker from "rsuite/DateRangePicker";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { useGetDashboardDataQuery } from "../../services/API/dashboardApiSlice";

import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TableHead,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  format,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { DateRangePickerProps } from "rsuite";
import { useAppSelector } from "../../app/hooks";
import { useAppDispatch } from "../../app/hooks";
import { setIsRemotePayment } from "../../features/dashboard/dashboardSlice";
import { RootState } from "../../app/store";
import { LocaleHelper } from "../../helpers/LocaleHelper";

const Ranges: DateRangePickerProps["ranges"] = [
  {
    label: "Today",
    value: [startOfDay(new Date()), endOfDay(new Date())],
    placement: "left",
  },

  {
    label: "Yesterday",
    value: [
      startOfDay(addDays(new Date(), -1)),
      endOfDay(addDays(new Date(), -1)),
    ],
    placement: "left",
  },

  {
    label: "Last 7 Days",
    value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
    placement: "left",
  },

  {
    label: "This Week",
    value: [startOfWeek(new Date()), endOfWeek(new Date())],
    placement: "left",
  },

  {
    label: "Next Week",
    closeOverlay: false,
    value: (value) => {
      const [start = new Date()] = value || [];
      return [
        addDays(startOfWeek(start, { weekStartsOn: 0 }), 7),
        addDays(endOfWeek(start, { weekStartsOn: 0 }), 7),
      ];
    },
    placement: "left",
  },
];

let dollarUSLocale = LocaleHelper.getCurrencyFormatter("US");

const DashboardApp = () => {
  const dispatch = useAppDispatch();

  const [transactionType, setTransactionType] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    startOfDay(subDays(new Date(), 6)),
    endOfDay(new Date()),
  ]);

  const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);
  const isRemotePayment = useAppSelector(
    (state: RootState) => state.dashboard.isRemotePayment
  );

  //! Data Fetching
  const { data, isFetching } = useGetDashboardDataQuery({
    dealerId,
    isRemotePayments: isRemotePayment,
    fromDate: format(dateRange[0], "yyyy-MM-dd"),
    toDate: format(dateRange[1], "yyyy-MM-dd"),
    transactionType,
  });

  // ----------------------- HANDLERS ---------------------------//
  const handleDateValueChange = (value: [Date, Date] | null) => {
    if (value) {
      setDateRange(value);
    }
  };

  const handleClose = () => {
    setDateRange([startOfDay(subDays(new Date(), 6)), endOfDay(new Date())]);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setIsRemotePayment(event.target.checked));
  };

  function GetCardNumber(row: any) {
    try {
      const myResult = JSON.parse(row.result);
      return myResult.maskedAccount;
    } catch (err) {
      return "";
    }
  }

  return (
    <>
      <Helmet>
        <title> Dashboard </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h6" gutterBottom>
            Dashboard
          </Typography>

          {/***** Filter Section *****/}
          <Box display="flex" justifyContent="center" alignItems="center">
            {/* Remote Payment */}
            <Stack direction={"row"} alignItems="center" mr={4}>
              <Typography variant="body2">Remote Payments</Typography>

              <Switch
                checked={isRemotePayment}
                onChange={handleSwitchChange}
                inputProps={{ "aria-label": "controlled" }}
              />
            </Stack>

            {/* Date range filter */}
            <Stack direction="row" alignItems="center" mr={4}>
              <DateRangePicker
                character=" -- "
                ranges={Ranges}
                size="lg"
                placeholder="Date Range"
                value={dateRange}
                onChange={handleDateValueChange}
                onClean={handleClose}
                format="M-d-yyyy"
                placement="autoVertical"
              />
            </Stack>

            {/* Transaction Type Filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="demo-select-small">Transaction Type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={transactionType}
                label="Transaction Type"
                onChange={(e) => setTransactionType(e.target.value)}
              >
                <MenuItem value="">--</MenuItem>
                <MenuItem value={"CreditSale"}>Credit Sale</MenuItem>
                <MenuItem value={"CreditReturn"}>Credit Return</MenuItem>
                <MenuItem value={"CreditAuth"}>Credit Auth</MenuItem>
                <MenuItem value={"CreditSaveCard"}>Credit Save Card</MenuItem>
                <MenuItem value={"RequestSignature"}>
                  Request Signature
                </MenuItem>
                <MenuItem value={"TokenizedPayment"}>
                  Tokenized Payment
                </MenuItem>
                <MenuItem value={"TokenizedRefund"}>Tokenized Refund</MenuItem>
                <MenuItem value={"TokenizedAuth"}>Tokenized Auth</MenuItem>
                <MenuItem value={"TokenizedForce"}>Tokenized Force</MenuItem>
                <MenuItem value={"VoidTransaction"}>Void Transaction</MenuItem>
                <MenuItem value={"RetrieveTransaction"}>
                  Retrieve Transaction
                </MenuItem>
                <MenuItem value={"RemotePayment"}>Remote Payment</MenuItem>
                <MenuItem value={"CancelPaymentLink"}>
                  Cancel Payment Link
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>

        <Grid container spacing={4} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                py: 1,
                boxShadow: 0,
                borderRadius: "10px",
                textAlign: "center",
                color: "#061b64",
                bgcolor: "#d1e9fc",
              }}
            >
              <Typography variant="subtitle1">
                Total Amount Processed
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.72 }}>
                {dollarUSLocale.format(Number(data?.totalAmountProcessed))}
                {/* ${data?.totalAmountProcessed} */}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                py: 1,
                boxShadow: 0,
                borderRadius: "10px",
                textAlign: "center",
                color: "#04297a",
                bgcolor: "#d0f2ff",
              }}
            >
              <Typography variant="subtitle1">
                Total Approved Transactions
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.72 }}>
                {data?.totalApprovedTransactions == null
                  ? 0
                  : data?.totalApprovedTransactions}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                py: 1,
                boxShadow: 0,
                borderRadius: "10px",
                textAlign: "center",
                color: " #7a4f01",
                bgcolor: "#fff7cd",
              }}
            >
              <Typography variant="subtitle1">Transaction Count</Typography>
              <Typography variant="h6" sx={{ opacity: 0.72 }}>
                {data?.transactions == null ? 0 : data?.transactions.rowCount}
              </Typography>
            </Card>
          </Grid>
        </Grid>
        <Typography variant="body1" mb={2} color="primary">
          Recent Transactions
        </Typography>
        <TableContainer component={Paper}>
          <Table
            size="small"
            sx={{ minWidth: 500 }}
            aria-label="custom pagination table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                <TableCell
                  align="left"
                  sx={{ fontWeight: "bold", width: "200px" }}
                >
                  Date
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>
                  Approval
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>
                  Type
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Total
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold" }}>
                  Card
                </TableCell>
                {/* <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  Action
                </TableCell> */}
              </TableRow>
            </TableHead>

            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">
                    <CircularProgress size="2rem" />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data?.transactions.data.length ? (
                    <>
                      {data?.transactions.data.map((row: any, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {row.customerName}
                          </TableCell>

                          <TableCell style={{ width: 160 }} align="left">
                            {/* {moment(row.transactionDate).format(
                              "M-D-YYYY, h:mm a"
                            )} */}
                            {moment
                              .utc(row.transactionDate)
                              .local()
                              .format("M/D/YYYY h:mm A")}
                          </TableCell>

                          <TableCell style={{ width: 160 }} align="left">
                            {row.approvalResultNumber}
                          </TableCell>

                          <TableCell style={{ width: 160 }} align="left">
                            {row.transactionType}
                          </TableCell>

                          <TableCell style={{ width: 160 }} align="right">
                            {dollarUSLocale.format(row.amountProcessed)}
                          </TableCell>

                          <TableCell style={{ width: 160 }} align="left">
                            {GetCardNumber(row)}
                          </TableCell>

                          {/* <TableCell style={{ width: 160 }} align="center">
                            <Button variant="outlined" color="error" style={{ textTransform: "none", padding: "00px" }}>
                              Void
                            </Button>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <Typography
                          color="primary"
                          sx={{ textAlign: "center" }}
                        >
                          No record found in this date range
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box style={{ padding: "5px", color: "gray" }}>
          {data?.transactions.data.length === 50 ? "50 transactions max" : ""}
        </Box>
      </Container>
    </>
  );
};

export default DashboardApp;
